import { useState } from 'react'
import { toast } from 'sonner'
import { Copy } from 'lucide-react'
import { Button } from 'tailwind-v4/components/button'
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from 'tailwind-v4/components/dialog'
import { Spinner } from 'tailwind-v4/components/spinner'
import { DateTimeLabel } from '@/components/ui/date-time-label'
import { TimeFormats } from '@/common/datepicker/utilities/formats'
import morpheusApi, {
  type SupportVerificationCode,
} from '@/state/morpheus/morpheus.slice'

function getErrorMessage(error: unknown, fallback: string): string {
  if (error && typeof error === 'object' && 'data' in error) {
    const data = (error as { data?: unknown }).data
    if (data && typeof data === 'object' && 'error' in data) {
      const message = (data as { error?: unknown }).error
      if (typeof message === 'string') return message
    }
  }
  return fallback
}

/**
 * Lets any authenticated user generate a code to read out to Stampede
 * support over the phone, so support can confirm the request is really
 * coming from them before making account changes. Looked up on the admin
 * side via the "Verify code" tool on `/admin/customers`.
 *
 * Fully controlled (`open`/`onOpenChange`) rather than owning its own
 * trigger: a trigger placed inside a menu (DropdownMenuItem, Headless UI
 * MenuItem) unmounts as soon as that menu closes on click, which would tear
 * this dialog down with it before it's readable. Callers own the trigger
 * and the open state; this component renders as a sibling of the menu, not
 * a descendant.
 */
export function SupportVerificationCodeDialog({
  open,
  onOpenChange,
}: {
  open: boolean
  onOpenChange: (open: boolean) => void
}) {
  const [result, setResult] = useState<SupportVerificationCode | null>(null)
  const [generate, { isLoading }] =
    morpheusApi.useGenerateSupportVerificationCodeMutation()

  const handleGenerate = async () => {
    try {
      const data = await generate().unwrap()
      setResult(data)
    } catch (error) {
      toast.error(
        getErrorMessage(error, 'Too many requests, please try again later')
      )
    }
  }

  return (
    <Dialog
      open={open}
      onOpenChange={(next) => {
        onOpenChange(next)
        if (!next) setResult(null)
      }}
    >
      <DialogContent className="sm:max-w-md">
        <DialogHeader>
          <DialogTitle>Verify your identity</DialogTitle>
          <DialogDescription>
            If you contact Stampede support by phone, generate a code here
            and read it out to them — it lets support confirm the request is
            really coming from you before they make any changes to your
            account.
          </DialogDescription>
        </DialogHeader>

        {result ? (
          <div className="flex flex-col items-center gap-2 rounded-lg border border-border p-4">
            <span className="font-mono text-2xl font-semibold tracking-widest text-foreground">
              {result.code}
            </span>
            <span className="text-xs text-muted-foreground">
              Expires{' '}
              <DateTimeLabel
                date={result.expires}
                format={TimeFormats.StandardDateMonthYearSmallHHmm}
              />
            </span>
          </div>
        ) : null}

        <div className="flex justify-end gap-2">
          {result ? (
            <Button
              variant="outline"
              leftIcon={<Copy className="size-4" />}
              onClick={() => {
                void navigator.clipboard.writeText(result.code)
                toast.success('Code copied')
              }}
            >
              Copy
            </Button>
          ) : null}
          <Button
            variant="primary"
            disabled={isLoading}
            leftIcon={isLoading ? <Spinner /> : undefined}
            onClick={() => void handleGenerate()}
          >
            {isLoading
              ? 'Generating…'
              : result
                ? 'Generate new code'
                : 'Generate code'}
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  )
}
